import React, { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { useWebViewLoading } from '../../../shared/hooks/useWebViewLoading';
import { isWebViewEnvironment, appBridge, postMessageToFlutter } from '../../../shared/services/bridge';
import './SignatureComplete.css';

type FeatureType = 'inquiry' | 'inspection';

const SignatureComplete: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const fromStatus = location.state?.fromStatus as '調査見積もり済' | '作業報告書確認' | 'result_confirmation';
  const featureType = (location.state?.featureType as FeatureType) ||
    (location.pathname.includes('/inspections/') ? 'inspection' : 'inquiry');

  const contentRef = useWebViewLoading(false);

  // 「ダッシュボードに戻る」ケース: Flutter側でボタンを表示するため、React側ではボタンを出さない
  // 見積もりサイン完了時・作業完了サイン完了時の両方でFlutter側に制御を委譲
  const isBackToList = featureType === 'inquiry';
  const isWebView = isWebViewEnvironment();

  // WebView環境: サイン完了画面では戻るボタンを非表示にする
  // （完了画面から戻ると再度APIを叩けてしまうため）
  useEffect(() => {
    if (isWebView) {
      appBridge.hideBackButton();

      // 一覧に戻るケース: Flutter側でフッターボタンを表示
      if (isBackToList) {
        postMessageToFlutter('signatureCompleteReady');
      }
    }
  }, [isBackToList, isWebView]);

  const handleNext = () => {
    // 点検の場合: 支払いなし→アンケートへ
    if (featureType === 'inspection') {
      navigate(`/inspections/${id}/survey`, { state: { featureType } });
    }
    // 修繕: 一覧へ戻る（ブラウザ環境のみ。WebView環境ではFlutter側の「完了」ボタンで制御）
    else {
      navigate('/inquiries', { replace: true });
    }
  };

  // タイトルとボタンテキストを fromStatus / featureType で動的に切替
  // PPTX指示（0506）: 注文サイン完了は「注文書の署名入力が完了しました」、納品サイン完了は「作業報告書の署名入力が完了しました」
  let title = 'サイン入力が完了しました';
  if (fromStatus === '調査見積もり済') {
    title = '注文書の署名入力が完了しました';
  } else if (fromStatus === '作業報告書確認') {
    title = '作業報告書の署名入力が完了しました';
  }

  let buttonText = 'トップに戻る';
  if (featureType === 'inspection') {
    buttonText = 'アンケートへ進む';
  }

  // WebView環境で一覧に戻るケースではフッターボタンを表示しない（Flutter側で表示）
  const showReactButton = !(isBackToList && isWebView);

  return (
    <div ref={contentRef} className="sign-complete-container">
      <main className="sign-complete-content">
        <img src="/assets/images/check-circle.png" alt="完了" className="sign-complete-icon" />
        <h2 className="sign-complete-title">{title}</h2>
      </main>
      {showReactButton && (
        <footer className="sign-complete-footer">
          <button className="button-primary" onClick={handleNext}>
            {buttonText}
          </button>
        </footer>
      )}
    </div>
  );
}

export default SignatureComplete;
